import { useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApi, useResource, errorText } from "@/lib/api";
import { formatAUD, roundCents } from "@/lib/format";
import { downloadCsv, downloadBlob } from "@/lib/csv";
import DirectorCard from "@/components/settlement/DirectorCard";
import YearByYearTable from "@/components/settlement/YearByYearTable";
import RecordSettlementDialog from "@/components/settlement/RecordSettlementDialog";
import { headline } from "@/components/settlement/headline";

const VIEWS = [{ v: "all", l: "All years" }, { v: "yearly", l: "Year by year" }];

const csvRows = (s, view) => {
  if (view === "all") {
    return [["Director", "Equal share", "Adjustment", "Total personal income", "Credits", "Debits", "Ticked-in misc & memos", "Result", "Headline"],
      ...s.directors.map((d) => [d.name, d.equal_share, d.adjustment, d.personal_income, d.credits, d.debits, d.extra, d.result, headline(d.result).text]),
      ["Total", "", "", "", "", "", "", s.total_result, ""]];
  }
  return [["FY", "FY net", ...s.directors.flatMap((d) => [`${d.name} share`, `${d.name} credits`, `${d.name} debits`, `${d.name} net`])],
    ...s.years.map((y, i) => [y.fy, y.net, ...s.directors.flatMap((d) => [d.years[i].share, d.years[i].credits, d.years[i].debits, d.years[i].value])]),
    ["Adjustments", "", ...s.directors.flatMap((d) => ["", "", "", d.adjustment])],
    ["Ticked-in misc & memos", "", ...s.directors.flatMap((d) => ["", "", "", d.extra])]];
};

export default function SettlementPage() {
  const api = useApi();
  const { data: s, reload } = useResource("/settlement");
  const [view, setView] = useState("all");
  const [recording, setRecording] = useState(null);

  const pdf = async (d) => {
    try {
      const blob = await api("get", `/settlement/statement/${d.id}?view=${view}`, undefined, { responseType: "blob" });
      downloadBlob(`${d.name}-statement-${view}.pdf`, blob);
    } catch (e) { toast.error(errorText(e)); }
  };

  return (
    <div className="rise">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="settlement-title">Settlement</h1>
      <p className="mt-3 text-muted-foreground text-base md:text-lg">What each director receives from, or owes, the company.</p>
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="flex border border-border bg-card p-1 rounded-full" data-testid="settlement-view-toggle">
          {VIEWS.map(({ v, l }) => (
            <button key={v} type="button" onClick={() => setView(v)} data-testid={`settlement-view-${v}`}
              className={`px-4 py-1.5 text-sm rounded-full transition-colors ${view === v ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        <Button variant="outline" className="ml-auto" disabled={!s} onClick={() => downloadCsv(`settlement-${view}.csv`, csvRows(s, view))} data-testid="settlement-export-csv">
          <FileSpreadsheet className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>
      {s && (
        <>
          <div className="mt-6 grid gap-4 grid-cols-1 md:grid-cols-3">
            <div className="border border-border bg-card p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Company net to date</div>
              <div className="font-mono text-2xl mt-2" data-testid="settlement-company-net">{formatAUD(s.company_net)}</div>
            </div>
            <div className="border border-border bg-card p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Equal share each</div>
              <div className="font-mono text-2xl mt-2" data-testid="settlement-equal-share">{formatAUD(s.directors[0]?.equal_share)}</div>
            </div>
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Combined total to settle</div>
              <div className={`font-mono text-2xl mt-2 ${roundCents(s.total_result).startsWith("-") ? "text-rose-300" : "text-emerald-300"}`} data-testid="settlement-total-result">{formatAUD(s.total_result)}</div>
            </div>
          </div>
          {view === "all" ? (
            <div className="mt-6 grid gap-4 grid-cols-1 lg:grid-cols-3">
              {s.directors.map((d) => <DirectorCard key={d.id} d={d} onPdf={() => pdf(d)} onRecord={() => setRecording(d)} />)}
            </div>
          ) : (
            <div className="mt-6 space-y-6">
              <YearByYearTable s={s} />
              <div className="flex flex-wrap gap-2">
                {s.directors.map((d) => (
                  <Button key={d.id} variant="outline" size="sm" onClick={() => pdf(d)} data-testid={`settlement-yearly-pdf-${d.name.toLowerCase()}`}>
                    {d.name} PDF (year by year)
                  </Button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <RecordSettlementDialog director={recording} onClose={() => setRecording(null)} onDone={() => { setRecording(null); reload(); }} />
    </div>
  );
}
